import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { ParticleMesh } from "../ParticleMesh";
import { MARKETPLACE_URL, MaskLine, CountUp } from "./shared";

const stats = [
  { to: 12480, suffix: "+", label: "Instances deployed" },
  { to: 99, suffix: ".9%", label: "Uptime on EC2" },
  { to: 90, suffix: "s", label: "Avg. launch time" },
];

const lines = ["YOUR HELPDESK.", "INSIDE YOUR EC2.", "ZERO DEPENDENCIES."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden pt-24"
    >
      <div className="absolute inset-0 grid-lines opacity-60" />
      <div className="absolute inset-0">
        <ParticleMesh />
      </div>
      <div className="pointer-events-none absolute -right-40 top-1/3 h-[420px] w-[420px] rounded-full bg-primary/20 blur-[140px]" />

      <motion.div style={{ y, opacity }} className="relative z-10 mx-auto w-full max-w-screen-2xl px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-primary" />
          <span className="label-mono">01 — Support ticketing, self-hosted</span>
        </motion.div>

        <h1 className="font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter text-white sm:text-6xl md:text-7xl lg:text-8xl">
          {lines.map((line, i) => (
            <MaskLine key={line} delay={0.25 + i * 0.12}>
              <span className={i === 2 ? "text-primary" : ""}>{line}</span>
            </MaskLine>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-8 max-w-xl font-sans text-base leading-relaxed text-neutral-400 md:text-lg"
        >
          A production-ready support ticket system delivered as an Amazon Machine Image. Deploy in one click,
          keep full root access, own every byte of your data.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <a
            href={MARKETPLACE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-marketplace-button"
            className="group flex items-center gap-3 bg-primary px-8 py-4 font-mono text-sm uppercase tracking-widest text-white transition-colors duration-200 hover:bg-[#FF7733]"
          >
            Launch in AWS Marketplace
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#docs"
            data-testid="hero-docs-button"
            className="border border-white/20 px-8 py-4 font-mono text-sm uppercase tracking-widest text-white transition-colors duration-200 hover:border-primary hover:text-primary"
          >
            Read the docs
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7 }}
          data-testid="hero-stats"
          className="mt-16 flex flex-wrap gap-10 border-t border-white/10 pt-8 sm:gap-16 md:mt-20"
        >
          {stats.map((s, i) => (
            <div key={s.label} data-testid={`hero-stat-${i}`} className="flex flex-col">
              <div className="flex items-center gap-2">
                {i === 0 && (
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                  </span>
                )}
                <CountUp
                  to={s.to}
                  suffix={s.suffix}
                  className="font-display text-3xl font-black tracking-tight text-white md:text-4xl"
                />
              </div>
              <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                {s.label}
              </span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="relative z-10 mx-auto mt-20 flex w-full max-w-screen-2xl items-center justify-between px-6 font-mono text-[11px] uppercase tracking-widest text-neutral-600 md:px-12"
      >
        <span className="flex items-center gap-2"><ArrowDown className="h-3 w-3" /> Scroll</span>
        <span>EC2 · AMI · v3.2</span>
      </motion.div>
    </section>
  );
};
